// Device registration: the models a user picks from, the registered check
// and the SDK-migration check, as the Flutter page does them
// (register_device.dart, drone_add_controller.dart,
// dji_controller_repository.dart _checkRegisteredDevices).

import type { CreateDeviceRequest, Device } from './api';

export interface DeviceModel {
  domain: number; // 0 aircraft, 2 remote controller
  deviceType: number;
  subType: number;
  title: string;
}

// DJI's (domain, type, sub type) triplets, from the Flutter page's
// DeviceTypeAutrik (lib/skymind/domain/device_enums.dart), itself aligned with
// the dashboard's; generated from it on 2026-10-21 (36 models).
export const DEVICE_MODELS: DeviceModel[] = [
  { domain: 0, deviceType: 103, subType: 0, title: 'Matrice 400' },  // M400RTK
  { domain: 0, deviceType: 89, subType: 0, title: 'Matrice 350 RTK' },  // M350RTK
  { domain: 0, deviceType: 60, subType: 0, title: 'Matrice 300 RTK' },  // M300RTK
  { domain: 0, deviceType: 67, subType: 0, title: 'Matrice 30' },  // MATRICE30
  { domain: 0, deviceType: 67, subType: 1, title: 'Matrice 30T' },  // MATRICE30T
  { domain: 0, deviceType: 77, subType: 0, title: 'Mavic 3E' },  // MAVIC3E
  { domain: 0, deviceType: 77, subType: 1, title: 'Mavic 3T' },  // MAVIC3T
  { domain: 0, deviceType: 77, subType: 3, title: 'Mavic 3TA' },  // MAVIC3TA
  { domain: 0, deviceType: 91, subType: 0, title: 'Matrice 3D' },  // MATRICE3D
  { domain: 0, deviceType: 91, subType: 1, title: 'Matrice 3DT' },  // MATRICE3DT
  { domain: 0, deviceType: 99, subType: 0, title: 'Matrice 4E' },  // MATRICE4E
  { domain: 0, deviceType: 99, subType: 1, title: 'Matrice 4T' },  // MATRICE4T
  { domain: 0, deviceType: 100, subType: 0, title: 'Matrice 4D' },  // MATRICE4D
  { domain: 0, deviceType: 100, subType: 1, title: 'Matrice 4DT' },  // MATRICE4DT
  { domain: 0, deviceType: 1001, subType: 0, title: 'DJI Mini 4 Pro' },  // DJIMINI4PRO
  { domain: 0, deviceType: 1002, subType: 0, title: 'DJI Mini 3' },  // DJIMINI3
  { domain: 0, deviceType: 1003, subType: 0, title: 'DJI Mini 3 Pro' },  // DJIMINI3PRO
  { domain: 0, deviceType: 1050, subType: 0, title: 'DJI Mini 2' },  // DJIMINI2
  { domain: 0, deviceType: 1051, subType: 0, title: 'DJI Mini SE' },  // DJIMINISE
  { domain: 0, deviceType: 1052, subType: 0, title: 'DJI Air 2S' },  // DJIAIR2S
  { domain: 0, deviceType: 1053, subType: 0, title: 'Mavic 2EA' },  // MAVIC2EA
  { domain: 0, deviceType: 1054, subType: 0, title: 'Mavic Air 2' },  // MAVICAIR2
  { domain: 0, deviceType: 1055, subType: 0, title: 'Mavic Mini' },  // MAVICMINI
  { domain: 0, deviceType: 1056, subType: 0, title: 'Mavic 2ED' },  // MAVIC2ED
  { domain: 0, deviceType: 1057, subType: 0, title: 'Mavic 2E' },  // MAVIC2E
  { domain: 0, deviceType: 1058, subType: 0, title: 'Mavic 2' },  // MAVIC2
  { domain: 0, deviceType: 1059, subType: 0, title: 'Mavic Air' },  // MAVICAIR
  { domain: 0, deviceType: 1060, subType: 0, title: 'Mavic Pro' },  // MAVICPRO
  { domain: 0, deviceType: 1061, subType: 0, title: 'Matrice 600' },  // MATRICE600
  { domain: 0, deviceType: 1062, subType: 0, title: 'Matrice 600 Pro' },  // MATRICE600PRO
  { domain: 2, deviceType: 56, subType: 0, title: 'Smart RC Ent.' },  // DJIRC
  { domain: 2, deviceType: 119, subType: 0, title: 'RC Plus' },  // DJIRCPLUS
  { domain: 2, deviceType: 174, subType: 0, title: 'RC Plus 2' },  // DJIRCPLUS2
  { domain: 2, deviceType: 144, subType: 0, title: 'RC Pro' },  // DJIRCPRO
  { domain: 2, deviceType: 244, subType: 0, title: 'DJI RC N1' },  // DJIRCN1
  { domain: 2, deviceType: 245, subType: 0, title: 'DJI RC N2' },  // DJIRCN2
];

export const aircraftModels = DEVICE_MODELS.filter((m) => m.domain === 0);
export const rcModels = DEVICE_MODELS.filter((m) => m.domain === 2);

/** A role with no model yet: the domain alone (0 aircraft, 2 remote controller). */
export const AIRCRAFT: DeviceModel = { domain: 0, deviceType: 0, subType: 0, title: 'Aircraft' };
export const REMOTE_CONTROLLER: DeviceModel = { domain: 2, deviceType: 0, subType: 0, title: 'Remote controller' };

/**
 * The model's name, once the platform knows it. The page registers a serial
 * with its role only: the bridge cannot tell the model, but Pilot announces
 * both the RC's and the aircraft's (domain, type, sub type) in its first
 * update_topo after connecting, and the platform writes them onto the
 * registered rows (go-pkgs devicestatus UpdateTopo). Type 0 = not yet.
 */
export function modelName(d: { domain?: number; device_type?: number; sub_type?: number } | undefined): string | undefined {
  if (!d?.device_type) return undefined;
  const m = DEVICE_MODELS.find((x) => x.domain === d.domain && x.deviceType === d.device_type && x.subType === (d.sub_type ?? 0));
  return m?.title ?? `type ${d.domain}-${d.device_type}-${d.sub_type ?? 0}`;
}

// Flickr base58, the alphabet of the Flutter page's ShortUuid.
const BASE58 = '123456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';

/** A random v4 UUID, base58-encoded and upper-cased: the Flutter page's stream id. */
export function newStreamId(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  const b = random(16);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  let n = 0n;
  for (const x of b) n = (n << 8n) | BigInt(x);
  let s = '';
  while (n > 0n) {
    s = BASE58[Number(n % 58n)] + s;
    n /= 58n;
  }
  return s.padStart(22, BASE58[0]!).toUpperCase();
}

/** What the platform already knows about this RC and its aircraft. */
export interface Registration {
  aircraft?: Device;
  rc?: Device;
}

export function findRegistration(devices: Device[], acSn: string, rcSn: string): Registration {
  return {
    aircraft: acSn ? devices.find((d) => d.device_sn === acSn) : undefined,
    rc: rcSn ? devices.find((d) => d.device_sn === rcSn) : undefined,
  };
}

/** Devices bound through the AutrikOS DJI app (MOBILESDK) that Pilot 2 now carries. */
export function needsSdkMigration(r: Registration, acSn: string, rcSn: string): { device: Device; label: string }[] {
  if (!acSn || !rcSn) return [];
  const out: { device: Device; label: string }[] = [];
  for (const [device, label] of [[r.aircraft, 'Aircraft'], [r.rc, 'RC']] as const) {
    if (device?.device_id && device.sdk?.toLowerCase() === 'dji_mobilesdk') out.push({ device, label });
  }
  return out;
}

/**
 * Devices registered with sdk "DJI": what this page and the Flutter page before
 * it sent, a value the dashboard does not know. It subscribes to a Pilot
 * device's topics only for DJI_CLOUDAPI, so such a device connects and reports
 * but shows nothing there (0 % battery). No question to ask: corrected on start.
 */
export function needsSdkCorrection(r: Registration): { device: Device; label: string }[] {
  const out: { device: Device; label: string }[] = [];
  for (const [device, label] of [[r.aircraft, 'Aircraft'], [r.rc, 'RC']] as const) {
    if (device?.device_id && device.sdk?.toUpperCase() === 'DJI') out.push({ device, label });
  }
  return out;
}

/**
 * The create request for one serial, as the Flutter page builds it (name
 * "Drone XXXX" / "RC XXXX"). A role with no model (type 0) leaves the type out:
 * fleet-api stores what is set, and Pilot's update_topo fills it in.
 */
export function createRequest(name: 'Drone' | 'RC', sn: string, model: DeviceModel): CreateDeviceRequest {
  const streamId = newStreamId();
  return {
    device_id: crypto.randomUUID(),
    device_sn: sn,
    device_name: `${name} ${streamId.substring(4, 8)}`,
    sdk: 'DJI_CLOUDAPI',
    stream_id: streamId,
    domain: model.domain,
    ...(model.deviceType ? { device_type: model.deviceType, sub_type: model.subType } : {}),
    is_sitl: false,
  };
}
